import {
  Badge,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Progress,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tooltip,
  Tr,
  VStack,
  chakra,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useKydEvent } from "../../hooks/useKydEvent";
import { useDialog, useDialogWithOptions } from "../../../../Common/Dialog";
import { useLoading } from "../../../hooks/form";
import actions from "../../../../actions";
import CurrencyInput from "react-currency-input-field";
import { useCallback, useEffect, useState } from "react";
import { FaCheck, FaEdit, FaPlus } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import { badgeForStatus } from "../../../Events/EventsTable";
import AppStoreBadge from "../../../../download-appstore.svg";
import LinkOrganizationDialog, {
  LinkOrganizationDialogContext,
  withLinkOrganizationDialog,
} from "./LinkOrganizationDialog";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import { formatDate } from "../../../../utils";
import dayjs from "dayjs";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { withConfirmDialog } from "../../ConfirmDialog";
import LinkedOrganizationsTable from "./LinkedOrganizationsTable";
import {
  EventDetailManagedCampaignsSection,
  KYDAdminEventDetailNavIcon,
} from "@common/interfaces/KYDEvent";
import { loadFbSdk } from "../Marketing/util";
import { DangerZone } from "./DangerZone";

const RedButton = ({
  title,
  children,
  onClick,
  buttonText,
  show,
  loading = false,
}) =>
  show ? (
    <Stack
      justifyContent={"space-between"}
      w="full"
      direction={["column", "row"]}
      h="full"
      alignItems={"center"}
    >
      <VStack justifyContent={"flex-start"}>
        <Text w="full" fontSize="lg">
          {title}
        </Text>
        {children}
      </VStack>
      <Button
        variant="outline"
        borderColor="kydRed.200"
        color="kydRed.200"
        onClick={onClick}
        disabled={loading}
        isDisabled={loading}
        isLoading={loading}
      >
        {buttonText}
      </Button>
    </Stack>
  ) : null;

const ColoredText = chakra.span;
const SettingsNavSection = () => {
  const { event_id } = useParams();
  const { kydEvent, refetch } = useKydEvent(event_id);
  const { open, close } = useDialogWithOptions();
  const [breakeven, setBreakeven] = useState("");
  const [isEditingBreakeven, setIsEditingBreakeven] = useState(false);
  const [editingLinkedOrganization, setEditingLinkedOrganization] =
    useState<KYDOrganizationEventLink | null>(null);
  const [loading, setLoading] = useLoading({
    settings: false,
    link_organization: false,
  });
  const toast = useToast();
  const dialog = useDialogWithOptions();
  const navigate = useNavigate();
  const {
    open: openLinkOrganizationDialog,
    close: closeLinkOrganizationDialog,
  } = useDialogWithOptions(LinkOrganizationDialogContext);

  useEffect(() => {
    if (kydEvent) {
      setBreakeven(
        kydEvent.breakeven ? (kydEvent.breakeven.amount / 100).toString() : "0"
      );
    }
  }, [kydEvent]);

  const onClickArchive = () => {
    open({
      title: `Archive event confirmation "${kydEvent?.name}"`,
      text: `Are you sure you want to archive this event? Event will be moved to draft and not visible to fans.`,
      actions: [
        {
          type: "archive",
          text: "Keep the event",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "archive",
          text: "Archive",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.deleteEvent(kydEvent.id);
              toast({
                status: "success",
                title: "Success!",
                description: "Event archived.",
              });
              await refetch({ silent: true });
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to archive event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onClickCancel = () => {
    open({
      title: `Event cancellation confirmation`,
      text: [
        `By cancelling this event, you will no longer be able to sell tickets and the event will not be visible to fans.`,
      ].join(" "),
      actions: [
        {
          type: "confirm",
          text: "Go Back",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "delete",
          text: "Cancel Event",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.cancelEvent(kydEvent.id);
              await refetch({ silent: true });
              toast({
                status: "success",
                title: "Success!",
                description: `${kydEvent?.name} has been successfully canceled.`,
              });
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to cancel event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onClickDelete = () => {
    open({
      title: `Event deletion confirmation`,
      text: [
        `By deleting this event, you will permanently delete the description, images, ticket types`,
        "promo codes and any specific configurations set.",
      ].join(" "),
      actions: [
        {
          type: "confirm",
          text: "Keep the event",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "delete",
          text: "Permanent delete",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.deleteEvent(kydEvent.id);
              toast({
                status: "success",
                title: "Success!",
                description: `${kydEvent?.name} has been successfully deleted.`,
              });
              navigate(`/events`);
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to delete event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onEditBreakeven = async () => {
    setLoading("settings", true);
    close();
    try {
      await actions.ADMIN.updateBreakeven(kydEvent.id, parseInt(breakeven));
      toast({
        status: "success",
        title: "Success!",
        description: "Breakeven updated",
      });
      await refetch({ silent: true });
    } catch (err) {
      toast({
        status: "error",
        title: "Update Failed!",
        description: "Failed to update breakeven amount",
      });
    }

    setIsEditingBreakeven(false);
    setLoading("settings", false);
  };

  const calculatePercentage = (breakeven: number, gmv: number) => {
    if (!breakeven || !gmv) return 0;

    return gmv / breakeven;
  };

  const onClickEditLinkedOrganization = (item: KYDOrganizationEventLink) => {
    setEditingLinkedOrganization(item);
    openLinkOrganizationDialog();
  };

  const onClickDeleteLinkedOrganization = async (
    item: KYDOrganizationEventLink
  ) => {
    dialog.open({
      title: `Stop sharing with ${item.organization.name}`,
      text: `Are you sure you want to stop sharing the event with "${item.organization.name}"?`,
      actions: [
        {
          type: "confirm",
          text: "Delete",
          buttonProps: {
            background: "red",
            color: "white",
          },
          onClick: async () => {
            try {
              setLoading("link_organization", true);
              await actions.ADMIN.disableOrganizationLinkForEvent(
                item.event_id,
                item.organization_id
              );
              await refetch();
              toast({
                duration: 2000,
                status: "success",
                title: "Organization link removed!",
                description: `The link with organization "${item.organization.name}" was deleted. This organization will no longer be able to see your event's data.`,
              });
            } catch (e) {
              toast({
                status: "error",
                title: "Error",
                description: e.message,
              });
            } finally {
              setLoading("link_organization", false);
              dialog.close();
              closeLinkOrganizationDialog();
            }
          },
        },
        {
          type: "cancel",
          text: "Cancel",
        },
      ],
    });
  };

  const onLinkOrganization = async (
    data: Partial<KYDOrganizationEventLink>
  ) => {
    try {
      const isEdit = !!data.created_at;

      setLoading("link_organization", true);
      if (isEdit) {
        await actions.ADMIN.updateLinkedOrganization(kydEvent.id, data);
      } else {
        await actions.ADMIN.linkOrganizationToEvent(kydEvent.id, data);
      }
      await refetch();
      closeLinkOrganizationDialog();
      toast({
        duration: 2000,
        status: "success",
        title: "Success",
        description: `Organization ${
          isEdit ? "permissions updated" : "linked"
        }! Your event will be visible on the "${
          data.organization?.name || data.organization_id
        }" organization ${isEdit ? "with the updated permissions" : ""}.`,
      });
    } catch (e) {
      toast({
        status: "error",
        title: "Failed to link organization",
        description:
          "It was not possible to link the organization. Please contact support",
      });
    } finally {
      setLoading("link_organization", false);
    }
  };

  const progressPercentage = calculatePercentage(
    parseInt(breakeven, 10),
    kydEvent?.total_gmv
  );

  return (
    <Stack w="full" gap={8}>
      <LinkOrganizationDialog
        onDelete={onClickDeleteLinkedOrganization}
        onClose={() => {
          setEditingLinkedOrganization(null);
        }}
        kydEvent={kydEvent}
        onSave={onLinkOrganization}
        editingLinkedOrganization={editingLinkedOrganization}
        isLoading={loading.link_organization}
      />
      <Heading w="full" size="md">
        Settings
      </Heading>
      {kydEvent ? (
        <HStack>
          <Text>Event Status:</Text>
          {badgeForStatus(kydEvent.status)}
        </HStack>
      ) : null}
      <Stack w="full" maxW={"400px"}>
        <Text fontWeight={"bold"} width={"full"}>
          Breakeven
        </Text>
        <VStack
          w="full"
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
        >
          <Text>GMV: {kydEvent?.total_gmv_display}</Text>
          <Text w="full">Breakeven amount:</Text>
          <HStack>
            <CurrencyInput
              //@ts-ignore
              w="55%"
              maxWidth={"300px"}
              placeholder="$"
              value={breakeven}
              disabled={!isEditingBreakeven}
              isDisabled={!isEditingBreakeven}
              onValueChange={setBreakeven}
              intlConfig={{
                locale: "en-US",
                currency: kydEvent?.currency,
              }}
              customInput={Input}
              step={1}
            />
            {isEditingBreakeven ? (
              <>
                <IconButton
                  icon={<FaCheck />}
                  isDisabled={!breakeven}
                  disabled={!breakeven}
                  aria-label="Confirm edit breakeven"
                  onClick={onEditBreakeven}
                />
                <IconButton
                  icon={<MdCancel />}
                  aria-label="Cancel edit breakeven"
                  onClick={() => {
                    setIsEditingBreakeven(false);
                    setBreakeven("");
                  }}
                />
              </>
            ) : (
              <IconButton
                onClick={() => setIsEditingBreakeven(true)}
                icon={<FaEdit />}
                aria-label="Edit breakeven"
              />
            )}
          </HStack>
          <Progress
            size="md"
            w="full"
            rounded={"lg"}
            colorScheme={"green"}
            value={progressPercentage}
          />
          <Text>{progressPercentage.toFixed(0)}%</Text>
        </VStack>
      </Stack>
      <Stack w="full" maxW={"400px"}>
        <Text fontWeight={"bold"} width={"full"}>
          Box Office App
        </Text>
        <VStack
          w="full"
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
        >
          <Link
            isExternal
            href="https://apps.apple.com/us/app/kyd-labs-box-office/id6494989064"
          >
            <Image src={AppStoreBadge} alt="Download on the App Store" />
          </Link>
        </VStack>
      </Stack>
      <Stack w="full">
        <HStack w="full" justifyContent={"space-between"}>
          <Text fontWeight={"bold"} width={"full"}>
            Share with another organization
          </Text>

          <IconButton
            variant={"outline"}
            aria-label="Share event with another organization"
            icon={<FaPlus />}
            onClick={() => {
              openLinkOrganizationDialog();
            }}
          />
        </HStack>
        <VStack w="full" overflowX={"scroll"}>
          <LinkedOrganizationsTable
            linked_organizations={kydEvent?.linked_organizations}
            onEdit={onClickEditLinkedOrganization}
            onCreate={openLinkOrganizationDialog}
            isLoading={loading.link_organization}
          />
        </VStack>
      </Stack>
      {kydEvent && kydEvent.display_options?.delete_action?.length > 0 ? (
        <DangerZone
          kydEvent={kydEvent}
          onArchive={onClickArchive}
          onCancel={onClickCancel}
          onDelete={onClickDelete}
          loading={loading.settings}
        />
      ) : null}
    </Stack>
  );
};

export default withConfirmDialog(
  withLinkOrganizationDialog(SettingsNavSection)
);
